/**
 * Lays a ReportModel out in the Ryoma Word template (ryoma-template.docx).
 *
 * The template is a style sheet by example: it holds one of each building block (cover, title,
 * summary box, numbered headings, body text, list, navy table, gold note box). Each block is
 * found by its example text, copied, and filled. The cover, logo, header, fonts and colours all
 * come from the template itself, so a founder can restyle the template without code changes,
 * as long as the example blocks stay in it.
 */
import JSZip from "jszip";
import type { Block, ReportModel } from "./report-model.ts";
import type { Motto } from "./brand.ts";

/** Brand colours from the founders' brief (A9 Brand). */
const NAVY = "0E2A47";
const GOLD = "8C6B33";
const GREY = "5A6068";
const STAMP_ID = "rIdRyomaChecked";
const STAMP_PATH = "media/ryoma-checked.png";

const esc = (s: string) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
const textOf = (xml: string) =>
  [...xml.matchAll(/<w:t(?:\s[^>]*)?>([\s\S]*?)<\/w:t>/g)].map((m) => m[1] ?? "").join("").replace(/&amp;/g, "&").replace(/&lt;/g, "<").replace(/&gt;/g, ">");

/** Split XML into its top-level <w:p>, <w:tbl> and <w:sectPr> elements. */
export function topLevel(body: string): string[] {
  const out: string[] = [];
  let depth = 0;
  let start = 0;
  for (const m of body.matchAll(/<(\/?)w:(p|tbl|sectPr)\b[^>]*?(\/?)>/g)) {
    const [, close, , selfClose] = m;
    const at = m.index!;
    if (selfClose) {
      if (depth === 0) out.push(m[0]);
      continue;
    }
    if (!close) {
      if (depth === 0) start = at;
      depth++;
    } else {
      depth--;
      if (depth === 0) out.push(body.slice(start, at + m[0].length));
    }
  }
  return out;
}

/** A copy of a paragraph with new text: keeps its paragraph settings and its first run's formatting. */
export function withText(para: string, text: string): string {
  const open = /^<w:p\b[^>]*>/.exec(para)?.[0] ?? "<w:p>";
  const pPr = /<w:pPr>[\s\S]*?<\/w:pPr>/.exec(para)?.[0] ?? "";
  const rPr = /<w:r\b[^>]*>\s*(<w:rPr>[\s\S]*?<\/w:rPr>)/.exec(para)?.[1] ?? "";
  const runs = text
    .split("\n")
    .map((line) => `<w:t xml:space="preserve">${esc(line)}</w:t>`)
    .join("<w:br/>");
  return `${open}${pPr}<w:r>${rPr}${runs}</w:r></w:p>`;
}

const cellsOf = (row: string) => [...row.matchAll(/<w:tc>[\s\S]*?<\/w:tc>/g)].map((m) => m[0]);
const rowsOf = (tbl: string) => [...tbl.matchAll(/<w:tr\b[\s\S]*?<\/w:tr>/g)].map((m) => m[0]);
const parasOf = (xml: string) => [...xml.matchAll(/<w:p\b[\s\S]*?<\/w:p>/g)].map((m) => m[0]);

/** A copy of a single-cell box table (summary or note) holding the given paragraphs. */
function boxWith(box: string, paragraphs: string[]): string {
  const cell = cellsOf(box)[0]!;
  const tcPr = /<w:tcPr>[\s\S]*?<\/w:tcPr>/.exec(cell)?.[0] ?? "";
  const newCell = `<w:tc>${tcPr}${paragraphs.join("")}</w:tc>`;
  // A box is never split across two pages.
  return box.replace(cell, () => newCell).replace(/<w:tr>(?!\s*<w:trPr>)/, "<w:tr><w:trPr><w:cantSplit/></w:trPr>");
}

/** The paragraph stays on the same page as whatever follows it. */
function keepNext(para: string): string {
  if (para.includes("<w:keepNext")) return para;
  if (!para.includes("<w:pPr>")) return para.replace(/^(<w:p\b[^>]*>)/, "$1<w:pPr><w:keepNext/></w:pPr>");
  // Word expects keepNext straight after the paragraph style, if there is one.
  return /<w:pPr><w:pStyle\b[^>]*\/>/.test(para)
    ? para.replace(/(<w:pPr><w:pStyle\b[^>]*\/>)/, "$1<w:keepNext/>")
    : para.replace("<w:pPr>", "<w:pPr><w:keepNext/>");
}

function cellWith(cell: string, text: string, width: number, keepWithNext = false): string {
  let para = withText(parasOf(cell)[0]!, text);
  // Keeps a short table on one page: each row stays with the row below it.
  if (keepWithNext) para = keepNext(para);
  return cell
    .replace(/<w:tcW\b[^>]*\/>/, `<w:tcW w:type="dxa" w:w="${width}"/>`)
    .replace(/<w:p\b[\s\S]*<\/w:p>/, () => para);
}

/** Tables up to this many body rows are kept on one page. */
const KEEP_TOGETHER_ROWS = 6;

export interface Protos {
  cover: string[];
  title: string;
  description: string;
  version: string;
  summary: string;
  spacer: string;
  heading1: string;
  heading2: string;
  body: string;
  list: string;
  table: string;
  note: string;
  sectPr: string;
}

export function findProtos(documentXml: string): Protos {
  const bodyStart = documentXml.indexOf("<w:body>");
  const bodyEnd = documentXml.lastIndexOf("</w:body>");
  if (bodyStart < 0 || bodyEnd < 0) throw new Error("The template has no document body");
  const els = topLevel(documentXml.slice(bodyStart + 8, bodyEnd));
  const find = (what: string, test: (el: string) => boolean): number => {
    const i = els.findIndex(test);
    if (i < 0) throw new Error(`The template is missing its example ${what}. Restore it from the original ryoma-template.docx.`);
    return i;
  };
  const isP = (el: string) => el.startsWith("<w:p");
  const isTbl = (el: string) => el.startsWith("<w:tbl");
  const titleAt = find("title (\"Document Title\")", (e) => isP(e) && textOf(e).trim() === "Document Title");
  return {
    cover: els.slice(0, titleAt),
    title: els[titleAt]!,
    description: els[find("description line", (e) => isP(e) && textOf(e).includes("One-line description"))]!,
    version: els[find("version line", (e) => isP(e) && /^Version\b/.test(textOf(e).trim()))]!,
    summary: els[find("summary box (\"WHAT THIS IS\")", (e) => isTbl(e) && textOf(e).includes("WHAT THIS IS"))]!,
    spacer: els[find("empty paragraph", (e) => isP(e) && textOf(e).trim() === "" && !e.includes("<w:drawing") && !e.includes("<w:pBdr"))]!,
    heading1: els[find("Heading 1", (e) => isP(e) && e.includes('w:val="Heading1"'))]!,
    heading2: els[find("Heading 2", (e) => isP(e) && e.includes('w:val="Heading2"'))]!,
    body: els[find("body text", (e) => isP(e) && textOf(e).startsWith("Body text"))]!,
    list: els[find("list point", (e) => isP(e) && /^\S\s+First point/.test(textOf(e)))]!,
    table: els[find("table (\"Column 1\")", (e) => isTbl(e) && textOf(e).includes("Column 1"))]!,
    note: els[find("note box", (e) => isTbl(e) && textOf(e).includes("Note box"))]!,
    sectPr: els[find("page settings", (e) => e.startsWith("<w:sectPr"))]!,
  };
}

/** The bullet the template's list example uses (its first character), e.g. "—". */
const bulletOf = (list: string) => textOf(list).match(/^(\S+)(\s+)/) ?? ["", "•", "  "];

/** A tighter table, for detail rows: smaller type, less padding. */
function compact(tbl: string): string {
  return tbl
    .replace(/<w:tcMar>[\s\S]*?<\/w:tcMar>/g, (m) => m.replace(/(<w:(?:top|bottom)\b[^>]*w:w=")\d+"/g, '$150"'))
    .replace(/w:line="288"/g, 'w:line="252"')
    .replace(/<w:sz w:val="17"\/>/g, '<w:sz w:val="16"/>')
    .replace(/<w:szCs w:val="17"\/>/g, '<w:szCs w:val="16"/>');
}

export function renderTable(p: Protos, headers: string[], rows: string[][], numeric: number[] = [], widths?: number[], small = false): string {
  const tbl = p.table;
  const tblPr = /<w:tblPr>[\s\S]*?<\/w:tblPr>/.exec(tbl)?.[0] ?? "";
  const totalWidth = Number(/<w:tblW\b[^>]*w:w="(\d+)"/.exec(tblPr)?.[1] ?? "9070");
  const protoRows = rowsOf(tbl);
  if (protoRows.length < 3) throw new Error("The template's example table needs a header row and two body rows");
  const trPr = (row: string) => /<w:trPr>[\s\S]*?<\/w:trPr>/.exec(row)?.[0] ?? "";
  const head = cellsOf(protoRows[0]!);
  const bodyA = cellsOf(protoRows[1]!);
  const bodyB = cellsOf(protoRows[2]!);
  // Example columns: 0 = row label (bold), 1 = number (right-aligned), 2 = plain text
  const pick = (cells: string[], col: number) => (col === 0 ? cells[0]! : numeric.includes(col) ? cells[1]! : cells[2] ?? cells[1]!);

  const cols = headers.length;
  const raw = widths && widths.length === cols ? widths : Array(cols).fill(1);
  const sum = raw.reduce((a, b) => a + b, 0);
  const w = raw.map((x) => Math.floor((x / sum) * totalWidth));
  w[cols - 1]! += totalWidth - w.reduce((a, b) => a + b, 0);

  const grid = `<w:tblGrid>${w.map((x) => `<w:gridCol w:w="${x}"/>`).join("")}</w:tblGrid>`;
  const together = rows.length <= KEEP_TOGETHER_ROWS;
  const headerRow = `<w:tr>${trPr(protoRows[0]!)}${headers.map((t, c) => cellWith(head[Math.min(c, head.length - 1)]!, t, w[c]!, rows.length > 0)).join("")}</w:tr>`;
  const bodyRows = rows.map((r, i) => {
    const cells = i % 2 === 0 ? bodyA : bodyB;
    const proto = i % 2 === 0 ? protoRows[1]! : protoRows[2]!;
    const keep = together && i < rows.length - 1;
    return `<w:tr>${trPr(proto)}${r.map((t, c) => cellWith(pick(cells, c), t, w[c]!, keep)).join("")}</w:tr>`;
  });
  const out = `<w:tbl>${tblPr}${grid}${headerRow}${bodyRows.join("")}</w:tbl>`;
  return small ? compact(out) : out;
}

const run = (text: string, opts: { font: string; size: number; color: string; bold?: boolean; italic?: boolean; spacing?: number }) =>
  `<w:r><w:rPr><w:rFonts w:ascii="${opts.font}" w:hAnsi="${opts.font}" w:cs="${opts.font}" w:eastAsia="${opts.font}"/>` +
  `${opts.bold ? "<w:b/><w:bCs/>" : '<w:b w:val="false"/><w:bCs w:val="false"/>'}${opts.italic ? "<w:i/><w:iCs/>" : ""}<w:color w:val="${opts.color}"/>` +
  `${opts.spacing ? `<w:spacing w:val="${opts.spacing}"/>` : ""}<w:sz w:val="${opts.size}"/><w:szCs w:val="${opts.size}"/></w:rPr>` +
  `<w:t xml:space="preserve">${esc(text)}</w:t></w:r>`;

/** The line under a heading: a title in gold capitals, then what the part is about, with a gold edge. */
export function mottoPara(motto: Motto): string {
  return (
    `<w:p><w:pPr><w:keepNext/><w:pBdr><w:left w:val="single" w:color="${GOLD}" w:sz="18" w:space="8"/></w:pBdr><w:spacing w:before="0" w:after="200"/><w:ind w:left="160"/></w:pPr>` +
    run(motto.title.toUpperCase(), { font: "Segoe UI", size: 17, color: GOLD, bold: true, spacing: 20 }) +
    run(`   ${motto.line}`, { font: "Cambria", size: 20, color: GREY, italic: true }) +
    `</w:p>`
  );
}

/** One big figure with a line under it. */
export function heroParas(value: string, label: string): string {
  return (
    `<w:p><w:pPr><w:keepNext/><w:spacing w:before="120" w:after="40"/></w:pPr>${run(value, { font: "Segoe UI", size: 52, color: NAVY, bold: true })}</w:p>` +
    `<w:p><w:pPr><w:keepNext/><w:spacing w:before="0" w:after="200"/></w:pPr>${run(label, { font: "Cambria", size: 21, color: GOLD, italic: true })}</w:p>`
  );
}

/**
 * An inline picture, copied from the cover's logo paragraph so it is placed the way the template
 * places images. Size in EMU (1 dxa = 635 EMU). Returns "" when the template has no image.
 */
export function imagePara(cover: string[], relId: string, cx: number, cy: number, docPrId: number, name: string, pPr: string): string {
  const logo = cover.find((e) => e.includes("<w:drawing"));
  if (!logo) return "";
  return logo
    .replace(/<w:pPr>[\s\S]*?<\/w:pPr>/, pPr)
    .replace(/cx="\d+" cy="\d+"/g, `cx="${cx}" cy="${cy}"`)
    .replace(/r:embed="[^"]*"/, `r:embed="${relId}"`)
    .replace(/<wp:docPr\b[^>]*\/>/, `<wp:docPr id="${docPrId}" name="${name} ${docPrId}" descr="${name}"/>`);
}

const NO_BORDERS = '<w:tblBorders><w:top w:val="nil"/><w:left w:val="nil"/><w:bottom w:val="nil"/><w:right w:val="nil"/><w:insideH w:val="nil"/><w:insideV w:val="nil"/></w:tblBorders>';
const tightPara = (inner = "", jc = "left") => `<w:p><w:pPr><w:keepNext/><w:spacing w:before="0" w:after="0" w:line="240" w:lineRule="auto"/><w:jc w:val="${jc}"/></w:pPr>${inner}</w:p>`;
const cell = (width: number, inner: string, extra = "") => `<w:tc><w:tcPr><w:tcW w:type="dxa" w:w="${width}"/>${extra}<w:vAlign w:val="center"/></w:tcPr>${inner}</w:tc>`;

/**
 * Savings as horizontal bars: label, a navy bar drawn by a filled table cell, and the figure.
 * Native Word, so it prints and edits like the rest of the report.
 */
export function barsTable(rows: { label: string; value: number; text: string }[], totalWidth: number): string {
  const labelW = 2500, textW = 1900, barW = totalWidth - labelW - textW;
  const max = Math.max(...rows.map((r) => r.value), 1);
  const body = rows.map((r) => {
    const fill = r.value > 0 ? Math.max(120, Math.round((r.value / max) * (barW - 120))) : 0;
    const bar = fill > 0
      ? `<w:tbl><w:tblPr><w:tblW w:type="dxa" w:w="${barW - 120}"/><w:tblLayout w:type="fixed"/>${NO_BORDERS}<w:tblCellMar><w:left w:w="0" w:type="dxa"/><w:right w:w="0" w:type="dxa"/></w:tblCellMar></w:tblPr>` +
        `<w:tblGrid><w:gridCol w:w="${fill}"/>${barW - 120 - fill > 0 ? `<w:gridCol w:w="${barW - 120 - fill}"/>` : ""}</w:tblGrid>` +
        `<w:tr><w:trPr><w:trHeight w:val="220" w:hRule="exact"/></w:trPr>${cell(fill, tightPara(), `<w:shd w:val="clear" w:color="auto" w:fill="${NAVY}"/>`)}${barW - 120 - fill > 0 ? cell(barW - 120 - fill, tightPara()) : ""}</w:tr></w:tbl>` +
        `<w:p><w:pPr><w:spacing w:before="0" w:after="0" w:line="20" w:lineRule="exact"/></w:pPr></w:p>`
      : tightPara();
    return `<w:tr><w:trPr><w:cantSplit/></w:trPr>` +
      cell(labelW, tightPara(run(r.label, { font: "Segoe UI", size: 17, color: NAVY, bold: true }))) +
      cell(barW, bar) +
      cell(textW, tightPara(run(r.text, { font: "Segoe UI", size: 17, color: r.value > 0 ? NAVY : GREY, bold: r.value > 0 }), "right")) +
      `</w:tr>`;
  });
  return `<w:tbl><w:tblPr><w:tblW w:type="dxa" w:w="${totalWidth}"/><w:tblLayout w:type="fixed"/>${NO_BORDERS}<w:tblCellMar><w:top w:w="60" w:type="dxa"/><w:bottom w:w="60" w:type="dxa"/><w:left w:w="60" w:type="dxa"/><w:right w:w="60" w:type="dxa"/></w:tblCellMar></w:tblPr>` +
    `<w:tblGrid><w:gridCol w:w="${labelW}"/><w:gridCol w:w="${barW}"/><w:gridCol w:w="${textW}"/></w:tblGrid>${body.join("")}</w:tbl>`;
}

export interface RenderOptions {
  /** The "checked" stamp PNG (square); leave out to sign off without it */
  stamp?: Uint8Array | ArrayBuffer | null;
}

/** Page settings for the cover: the header (small logo) but no footer, so the cover has no page number. */
export function coverSectPr(sectPr: string): string {
  return sectPr.replace(/<w:footerReference\b[^>]*\/>/g, "").replace(/<w:pgNumType\b[^>]*\/>/, "");
}

/** Page settings for the report: page numbers start at 1 after the cover. */
export function bodySectPr(sectPr: string): string {
  return /<w:pgNumType\b/.test(sectPr)
    ? sectPr.replace(/<w:pgNumType\b[^>]*\/>/, '<w:pgNumType w:start="1"/>')
    : sectPr.replace(/<w:docGrid\b|<\/w:sectPr>/, (m) => `<w:pgNumType w:start="1"/>${m}`);
}

const withPageBreak = (para: string) =>
  para.includes("<w:pageBreakBefore")
    ? para.replace(/<w:pageBreakBefore\b[^>]*\/>/, '<w:pageBreakBefore w:val="1"/>')
    : para.includes("<w:pPr>") ? para.replace("<w:pPr>", "<w:pPr><w:pageBreakBefore/>") : para.replace(/^(<w:p\b[^>]*>)/, "$1<w:pPr><w:pageBreakBefore/></w:pPr>");

export function renderBody(model: ReportModel, p: Protos, opts: { stamp?: boolean } = {}): string {
  // The cover: no large logo (the header already carries it), so the title starts at the top.
  const out: string[] = [];
  out.push(withText(p.title, model.title), withText(p.description, model.description), withText(p.version, model.versionLine));

  const summaryParas = parasOf(cellsOf(p.summary)[0]!);
  const labelPara = summaryParas.find((x) => textOf(x).includes("WHAT THIS IS")) ?? summaryParas[0]!;
  const summaryBody = summaryParas.find((x) => x !== labelPara) ?? labelPara;
  out.push(boxWith(p.summary, [withText(labelPara, model.summaryLabel), ...model.summary.map((t) => withText(summaryBody, t))]));
  const pageWidth = Number(/<w:tblW\b[^>]*w:w="(\d+)"/.exec(p.table)?.[1] ?? "9070");
  let docPr = 900;
  const notePara = parasOf(cellsOf(p.note)[0]!)[0]!;
  const [, bullet, gap] = bulletOf(p.list);

  const smallGap = '<w:p><w:pPr><w:spacing w:before="0" w:after="0" w:line="200" w:lineRule="exact"/></w:pPr></w:p>';
  const renderBlocks = (blocks: Block[], num: number | null) => {
    let sub = 0;
    blocks.forEach((b, k) => {
      const next = blocks[k + 1]?.kind;
      // No gap after the last block on a page: it could push an empty line onto a page of its own.
      const gapAfter = k === blocks.length - 1 ? "" : smallGap;
      if (b.kind === "p") {
        // A line that introduces a table or box stays with it.
        const para = withText(p.body, b.text);
        out.push(next === "table" || next === "note" || next === "bars" ? keepNext(para) : para);
      } else if (b.kind === "h2") {
        sub++;
        out.push(keepNext(withText(p.heading2, num === null ? b.text : `${num}.${sub}  ${b.text}`)));
      } else if (b.kind === "list") out.push(...b.items.map((t) => withText(p.list, `${bullet}${gap}${t}`)));
      else if (b.kind === "table") out.push(renderTable(p, b.headers, b.rows, b.numeric, b.widths, b.small), gapAfter);
      else if (b.kind === "note") out.push(boxWith(p.note, b.lines.map((t) => withText(notePara, t))), gapAfter);
      else if (b.kind === "motto") out.push(mottoPara(b.motto));
      else if (b.kind === "hero") out.push(heroParas(b.value, b.label));
      else if (b.kind === "bars") out.push(barsTable(b.rows, pageWidth), gapAfter);
      else if (b.kind === "signoff") {
        // The "checked" stamp beside the sign-off lines; the lines alone when there is no stamp image.
        // The stamp is twice as wide as it is tall.
        const stamp = opts.stamp ? imagePara(p.cover, STAMP_ID, 1800 * 635, 900 * 635, docPr++, "Checked stamp", '<w:pPr><w:spacing w:before="0" w:after="0"/><w:jc w:val="center"/></w:pPr>') : "";
        const lines = b.lines.map((t, i) => `<w:p><w:pPr><w:spacing w:before="0" w:after="80" w:line="252" w:lineRule="auto"/></w:pPr>${run(t, { font: "Segoe UI", size: i === 0 ? 18 : 15, color: i === 0 ? NAVY : GREY, bold: i === 0 })}</w:p>`).join("");
        out.push(
          `<w:tbl><w:tblPr><w:tblW w:type="dxa" w:w="${pageWidth}"/><w:tblLayout w:type="fixed"/>${NO_BORDERS}</w:tblPr><w:tblGrid><w:gridCol w:w="2100"/><w:gridCol w:w="${pageWidth - 2100}"/></w:tblGrid>` +
          `<w:tr><w:trPr><w:cantSplit/></w:trPr>${cell(2100, stamp || tightPara())}${cell(pageWidth - 2100, lines)}</w:tr></w:tbl>`,
        );
      }
    });
  };

  renderBlocks(model.cover, null);

  // Contents: each part is one page, numbered from 1 after the cover.
  out.push(keepNext(withText(p.heading2, "In this report")));
  const numW = 700, pageW = 1300, nameW = pageWidth - numW - pageW;
  const line = (t: string, jc = "left", bold = false) =>
    `<w:p><w:pPr><w:keepNext/><w:spacing w:before="40" w:after="40"/><w:jc w:val="${jc}"/></w:pPr>${run(t, { font: "Segoe UI", size: 19, color: NAVY, bold })}</w:p>`;
  const rule = `<w:tcBorders><w:bottom w:val="single" w:color="D9C89E" w:sz="4"/></w:tcBorders>`;
  out.push(
    `<w:tbl><w:tblPr><w:tblW w:type="dxa" w:w="${pageWidth}"/><w:tblLayout w:type="fixed"/>${NO_BORDERS}</w:tblPr>` +
    `<w:tblGrid><w:gridCol w:w="${numW}"/><w:gridCol w:w="${nameW}"/><w:gridCol w:w="${pageW}"/></w:tblGrid>` +
    model.sections.map((sec, i) =>
      `<w:tr><w:trPr><w:cantSplit/></w:trPr>${cell(numW, line(String(i + 1), "left", true), rule)}${cell(nameW, line(sec.heading), rule)}${cell(pageW, line(`Page ${i + 1}`, "right"), rule)}</w:tr>`,
    ).join("") +
    `</w:tbl>`,
  );
  if (model.preparedFor) {
    out.push(smallGap, smallGap);
    out.push(renderTable(p, ["Prepared for", "Prepared by", "Contact"], [[model.preparedFor, model.preparedBy ?? "Ryoma Insight Ltd", model.contact ?? ""]], [], undefined, true));
  }
  // End of the cover: its own page settings, with no footer and so no page number.
  out.push(`<w:p><w:pPr><w:spacing w:before="0" w:after="0"/>${coverSectPr(p.sectPr)}</w:pPr></w:p>`);

  // The five parts, each from the top of a new page.
  model.sections.forEach((section, i) => {
    const num = i + 1;
    let h1 = withText(p.heading1, `${num}.  ${section.heading}`).replace(/<w:pageBreakBefore\b[^>]*\/>/, "");
    if (i > 0) h1 = withPageBreak(h1);
    out.push(h1);
    renderBlocks(section.blocks, num);
  });
  out.push(bodySectPr(p.sectPr));
  return out.join("");
}

/** The first run formatting in a footer, so a shown field result matches the text around it. */
const footerRPr = (xml: string) => /<w:rPr>[\s\S]*?<\/w:rPr>/.exec(xml)?.[0] ?? "";

export async function renderReport(template: ArrayBuffer | Uint8Array, model: ReportModel, options: RenderOptions = {}): Promise<Uint8Array> {
  const zip = await JSZip.loadAsync(template);
  const docFile = zip.file("word/document.xml");
  if (!docFile) throw new Error("Not a Word document (.docx)");
  const doc = await docFile.async("string");
  const protos = findProtos(doc);
  const bodyStart = doc.indexOf("<w:body>") + 8;
  const bodyEnd = doc.lastIndexOf("</w:body>");
  const hasImage = protos.cover.some((e) => e.includes("<w:drawing"));
  const withStamp = !!options.stamp && hasImage;
  zip.file("word/document.xml", doc.slice(0, bodyStart) + renderBody(model, protos, { stamp: withStamp }) + doc.slice(bodyEnd));
  const images: [boolean, string, string, Uint8Array | ArrayBuffer | null | undefined][] = [
    [withStamp, STAMP_ID, STAMP_PATH, options.stamp],
  ];
  for (const [use, relId, path, bytes] of images) {
    if (!use || !bytes) continue;
    zip.file(`word/${path}`, bytes);
    const relsName = "word/_rels/document.xml.rels";
    const rels = (await zip.file(relsName)?.async("string")) ?? '<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"></Relationships>';
    if (!rels.includes(relId)) {
      zip.file(relsName, rels.replace("</Relationships>", `<Relationship Id="${relId}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/image" Target="${path}"/></Relationships>`));
    }
    const ct = await zip.file("[Content_Types].xml")!.async("string");
    if (!/Extension="png"/i.test(ct)) zip.file("[Content_Types].xml", ct.replace("</Types>", '<Default Extension="png" ContentType="image/png"/></Types>'));
  }

  // Footer: "[Document] · Internal and Confidential" becomes "Audit report: Client · Confidential"
  for (const name of Object.keys(zip.files).filter((f) => /^word\/footer\d*\.xml$/.test(f))) {
    const xml = await zip.file(name)!.async("string");
    // "Page 1 of 5": the cover is its own section, so the count is of this section's pages.
    zip.file(name, xml.replace("[Document]", esc(model.documentName)).replace("Internal and Confidential", "Confidential")
      .replace(/(<w:instrText[^>]*>)NUMPAGES(<\/w:instrText>[\s\S]*?<w:fldChar w:fldCharType="separate"\/>)(<w:fldChar w:fldCharType="end"\/>)/g,
        (_m, a, b, c) => `${a}SECTIONPAGES${b}</w:r><w:r>${footerRPr(xml)}<w:t>${model.sections.length}</w:t></w:r><w:r>${footerRPr(xml)}${c}`));
  }
  const core = zip.file("docProps/core.xml");
  if (core) {
    const xml = await core.async("string");
    zip.file("docProps/core.xml", xml.replace(/<dc:title>[\s\S]*?<\/dc:title>|<dc:title\/>/, `<dc:title>${esc(model.documentName)}</dc:title>`));
  }
  return zip.generateAsync({ type: "uint8array", compression: "DEFLATE" });
}
